import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { normalizeCarMaterials } from './car3dMaterial.js';

export interface Car3dPreviewHandle {
  dispose(): void;
}

export interface Car3dPreviewOptions {
  /**
   * Fige le premier rendu dans une `<img>` puis libère le contexte WebGL.
   * Destiné aux grilles (sélecteur, bibliothèque) : trop de renderers
   * simultanés saturent la limite du navigateur (~8–16) et laissent des
   * canvas blancs « cassés ». L’aperçu live du détail voiture reste animé
   * (option absente / `false`).
   */
  snapshot?: boolean;
}

const loader = new GLTFLoader();
const SNAPSHOT_CACHE_MAX = 80;
const snapshotCache = new Map<string, string>();

/** Un seul renderer partagé pour toutes les vignettes figées (jamais dans le DOM). */
let snapshotRenderer: THREE.WebGLRenderer | null = null;
let snapshotGpuBusy = false;
const snapshotGpuWaiters: Array<() => void> = [];

/** Vide le cache de vignettes figées — réservé aux tests. */
export function clearCar3dPreviewSnapshotCache(): void {
  snapshotCache.clear();
  snapshotRenderer?.dispose();
  snapshotRenderer = null;
  snapshotGpuBusy = false;
  snapshotGpuWaiters.length = 0;
}

function rememberSnapshot(url: string, dataUrl: string): void {
  if (snapshotCache.has(url)) {
    snapshotCache.delete(url);
  } else if (snapshotCache.size >= SNAPSHOT_CACHE_MAX) {
    const first = snapshotCache.keys().next().value;
    if (first !== undefined) snapshotCache.delete(first);
  }
  snapshotCache.set(url, dataUrl);
}

function appendSnapshotImage(container: HTMLElement, dataUrl: string): void {
  const img = document.createElement('img');
  img.className = 'car-detail-3d-snapshot';
  img.alt = '';
  img.src = dataUrl;
  container.appendChild(img);
}

/**
 * Remplace un canvas WebGL par une image PNG du buffer courant.
 * @returns l’URL data, ou `null` si la capture est impossible.
 */
export function replaceWebGlCanvasWithSnapshot(
  container: HTMLElement,
  canvas: HTMLCanvasElement
): string | null {
  let dataUrl = '';
  try {
    dataUrl = canvas.toDataURL('image/png');
  } catch {
    return null;
  }
  if (!dataUrl.startsWith('data:image/')) return null;
  canvas.remove();
  appendSnapshotImage(container, dataUrl);
  return dataUrl;
}

function markPreviewEmpty(container: HTMLElement): void {
  container.classList.add('car3d-preview--empty');
}

function acquireSnapshotGpu(run: () => void): () => void {
  let cancelled = false;
  const task = (): void => {
    if (cancelled) {
      releaseSnapshotGpu();
      return;
    }
    run();
  };
  if (!snapshotGpuBusy) {
    snapshotGpuBusy = true;
    task();
  } else {
    snapshotGpuWaiters.push(task);
  }
  return () => {
    cancelled = true;
    const i = snapshotGpuWaiters.indexOf(task);
    if (i !== -1) snapshotGpuWaiters.splice(i, 1);
  };
}

function releaseSnapshotGpu(): void {
  const next = snapshotGpuWaiters.shift();
  if (next) next();
  else snapshotGpuBusy = false;
}

function getSnapshotRenderer(): THREE.WebGLRenderer {
  if (!snapshotRenderer) {
    snapshotRenderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      preserveDrawingBuffer: true,
      premultipliedAlpha: false,
    });
    snapshotRenderer.setClearColor(0x000000, 0);
    snapshotRenderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  }
  return snapshotRenderer;
}

function fitCarModel(model: THREE.Object3D): void {
  const box = new THREE.Box3().setFromObject(model);
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const scale = 1.7 / Math.max(size.x, size.y, size.z, 0.001);
  model.scale.setScalar(scale);
  model.position.set(-center.x * scale, -box.min.y * scale, -center.z * scale);
  normalizeCarMaterials(model);
}

/**
 * Rend `model` hors DOM dans le renderer partagé et retourne un PNG.
 * Un seul appel à la fois (file `acquireSnapshotGpu`) pour ne jamais
 * multiplier les contextes WebGL des grilles.
 */
function captureModelSnapshot(container: HTMLElement, model: THREE.Object3D): string | null {
  const width = Math.max(container.clientWidth || 0, 128);
  const height = Math.max(container.clientHeight || 0, 128);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(35, width / height, 0.1, 100);
  camera.position.set(2.4, 1.7, 3.2);
  camera.lookAt(0, 0.4, 0);
  scene.add(new THREE.HemisphereLight(0xffffff, 0x2a2a44, 1.5));
  const keyLight = new THREE.DirectionalLight(0xffffff, 1.3);
  keyLight.position.set(3, 5, 2);
  scene.add(keyLight);

  const carGroup = new THREE.Group();
  fitCarModel(model);
  carGroup.add(model);
  scene.add(carGroup);

  const renderer = getSnapshotRenderer();
  const gl = renderer.getContext();
  if (gl.isContextLost()) {
    snapshotRenderer?.dispose();
    snapshotRenderer = null;
    return null;
  }
  renderer.setSize(width, height);
  renderer.setClearColor(0x000000, 0);
  renderer.clear();
  renderer.render(scene, camera);

  if (gl.isContextLost()) return null;

  let dataUrl: string | null = null;
  try {
    const raw = renderer.domElement.toDataURL('image/png');
    if (raw.startsWith('data:image/')) dataUrl = raw;
  } catch {
    dataUrl = null;
  }

  scene.traverse((obj) => {
    if (obj instanceof THREE.Mesh) {
      obj.geometry.dispose();
      const material = obj.material;
      for (const m of Array.isArray(material) ? material : [material]) m.dispose();
    }
  });

  return dataUrl;
}

function mountSnapshotPreview(
  container: HTMLElement,
  modelUrl: string,
  onSettled?: () => void
): Car3dPreviewHandle {
  container.classList.remove('car3d-preview--empty');

  const cached = snapshotCache.get(modelUrl);
  if (cached) {
    appendSnapshotImage(container, cached);
    queueMicrotask(() => onSettled?.());
    return {
      dispose(): void {
        container.querySelector('.car-detail-3d-snapshot')?.remove();
      },
    };
  }

  let disposed = false;
  let cancelGpu: (() => void) | null = null;

  loader.load(
    modelUrl,
    (gltf) => {
      onSettled?.();
      if (disposed) return;
      cancelGpu = acquireSnapshotGpu(() => {
        try {
          if (disposed) return;
          const dataUrl = captureModelSnapshot(container, gltf.scene);
          if (dataUrl) {
            rememberSnapshot(modelUrl, dataUrl);
            appendSnapshotImage(container, dataUrl);
          } else {
            markPreviewEmpty(container);
          }
        } finally {
          releaseSnapshotGpu();
        }
      });
    },
    undefined,
    () => {
      if (!disposed) markPreviewEmpty(container);
      onSettled?.();
    }
  );

  return {
    dispose(): void {
      if (disposed) return;
      disposed = true;
      cancelGpu?.();
      cancelGpu = null;
      container.querySelector('.car-detail-3d-snapshot')?.remove();
      container.classList.remove('car3d-preview--empty');
    },
  };
}

/**
 * Monte un aperçu 3D auto-rotatif du modèle glTF dans `container` (POC three.js).
 * `onSettled`, si fourni, est appelé une fois le chargement réseau terminé (succès
 * ou échec) — pas au montage, qui est synchrone et immédiat. En mode `snapshot`,
 * le contexte WebGL n’est créé qu’après le chargement, hors DOM, et partagé entre
 * toutes les vignettes.
 */
export function mountCar3dPreview(
  container: HTMLElement,
  modelUrl: string,
  onSettled?: () => void,
  options?: Car3dPreviewOptions
): Car3dPreviewHandle {
  if (options?.snapshot) {
    return mountSnapshotPreview(container, modelUrl, onSettled);
  }

  container.classList.remove('car3d-preview--empty');

  const width = container.clientWidth || 260;
  const height = container.clientHeight || 200;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(35, width / height, 0.1, 100);
  camera.position.set(2.4, 1.7, 3.2);
  camera.lookAt(0, 0.4, 0);

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setSize(width, height);
  renderer.domElement.className = 'car-detail-3d-canvas';
  container.appendChild(renderer.domElement);

  scene.add(new THREE.HemisphereLight(0xffffff, 0x2a2a44, 1.5));
  const keyLight = new THREE.DirectionalLight(0xffffff, 1.3);
  keyLight.position.set(3, 5, 2);
  scene.add(keyLight);

  const carGroup = new THREE.Group();
  scene.add(carGroup);

  let disposed = false;
  let webglReleased = false;
  let frameId = 0;

  function releaseWebGl(): void {
    if (webglReleased) return;
    webglReleased = true;
    cancelAnimationFrame(frameId);
    resizeObserver.disconnect();
    scene.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry.dispose();
        const material = obj.material;
        for (const m of Array.isArray(material) ? material : [material]) m.dispose();
      }
    });
    renderer.dispose();
    renderer.domElement.remove();
  }

  const resizeObserver = new ResizeObserver(() => {
    if (webglReleased) return;
    const w = container.clientWidth || width;
    const h = container.clientHeight || height;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
  });
  resizeObserver.observe(container);

  loader.load(
    modelUrl,
    (gltf) => {
      if (disposed) {
        onSettled?.();
        return;
      }
      fitCarModel(gltf.scene);
      carGroup.add(gltf.scene);
      onSettled?.();
    },
    undefined,
    () => {
      if (!disposed) {
        markPreviewEmpty(container);
        releaseWebGl();
      }
      onSettled?.();
    }
  );

  function animate(): void {
    if (disposed || webglReleased) return;
    carGroup.rotation.y += 0.008;
    renderer.render(scene, camera);
    frameId = requestAnimationFrame(animate);
  }
  frameId = requestAnimationFrame(animate);

  return {
    dispose(): void {
      if (disposed) return;
      disposed = true;
      releaseWebGl();
      container.querySelector('.car-detail-3d-snapshot')?.remove();
      container.classList.remove('car3d-preview--empty');
    },
  };
}
